// ✅ LabourChargesBillGenerationEntry.jsx (EDIT + SAVE + LOCALSTORAGE HEADERS)

import React, { useEffect, useMemo, useState, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import PaymentLayoutWrapper from "../../components/PaymentLayoutWrapper";
import { FaArrowLeft, FaSearch } from "react-icons/fa";

const BASE_URL =
  import.meta?.env?.VITE_BASE_URL || "http://nissi-india.com";

// -------- helpers
const toDDMMYYYY = (yyyyMMdd) => {
  if (!yyyyMMdd) return "";
  const m = yyyyMMdd.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return "";
  return `${m[3]}/${m[2]}/${m[1]}`;
};

const toYYYYMMDD = (ddmmyyyy) => {
  if (!ddmmyyyy) return "";
  const m = ddmmyyyy.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return "";
  return `${m[3]}-${m[2]}-${m[1]}`;
};

const toFixed2 = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : "0.00";
};

const LabourChargesBillGenerationEntry = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();
  const location = useLocation();

  // ✅ localStorage helper
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS = getLS("branchId") || getLS("branchid");
  const branchTypeLS = getLS("branchType") || getLS("branchtype");
  const userId =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchIdLS,
      "x-branch": branchTypeLS,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchIdLS, branchTypeLS, userId]
  );

  // ✅ read billid from URL
  const billid = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const v = params.get("billid");
    return v ? Number(v) : null;
  }, [location.search]);

  // ✅ user/branch
  const branchid = branchIdLS || "";
  const loginuserid = userId || "";

  // dropdown data
  const [suppliers, setSuppliers] = useState([]);
  const [projects, setProjects] = useState([]);

  // ✅ screen state
  const [loading, setLoading] = useState(false);
  const [loadingBill, setLoadingBill] = useState(false);
  const [details, setDetails] = useState([]);

  // ✅ form state
  const [formData, setFormData] = useState({
    billGenNo: "",
    billDate: "",
    billedBy: "",
    remarks: "",
    fromDate: "",
    toDate: "",
    projectId: "",
    supplierId: "",
    othersAdditions: "",
    total: "",
    advance: "",
    otherDeductions: "",
    netAmount: "",
  });

  // ✅ total computed from detail rows + additions - deductions
  const computed = useMemo(() => {
    const sumAmount = details.reduce(
      (acc, r) => acc + Number(r.fld_TotalExpenditure || r.fld_amount || 0),
      0
    );
    const additions = Number(formData.othersAdditions || 0);
    const total = sumAmount + additions;

    const advance = Number(formData.advance || 0);
    const otherDed = Number(formData.otherDeductions || 0);
    const net = total - advance - otherDed;

    return {
      sumAmount,
      total,
      net,
    };
  }, [
    details,
    formData.othersAdditions,
    formData.advance,
    formData.otherDeductions,
  ]);

  useEffect(() => {
    setFormData((p) => ({
      ...p,
      total: toFixed2(computed.total),
      netAmount: toFixed2(computed.net),
    }));
  }, [computed.total, computed.net]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const getRows = (res) => {
    if (Array.isArray(res?.data)) return res.data;
    if (Array.isArray(res?.data?.data)) return res.data.data;
    return [];
  };

  const getObj = (res) => {
    if (res?.data?.data && typeof res.data.data === "object") return res.data.data;
    if (res?.data && typeof res.data === "object") return res.data;
    return null;
  };

  // ------------------------------------------------------------------
  // LOAD DDLs (suppliers/projects)
  // ------------------------------------------------------------------
  const loadDropdowns = async () => {
    try {
      if (!branchid) return;

      const [supRes, prjRes] = await Promise.all([
        axios.get(`${BASE_URL}/api/labourbill/suppliers`, {
          params: { branchid },
          headers: commonHeaders,
        }),
        axios.get(`${BASE_URL}/api/labourbill/projects`, {
          params: { branchid },
          headers: commonHeaders,
        }),
      ]);

      setSuppliers(getRows(supRes));
      setProjects(getRows(prjRes));
    } catch (e) {
      console.error(e);
      toast.error("Failed to load dropdowns");
    }
  };

  // ------------------------------------------------------------------
  // IF EDIT MODE -> load by billid
  // ------------------------------------------------------------------
  const loadBillById = async () => {
    if (!billid) return;

    try {
      setLoadingBill(true);

      const res = await axios.get(`${BASE_URL}/api/labourbill/getByBillId`, {
        params: { billid },
        headers: commonHeaders,
      });

      const data = getObj(res);

      if (!data?.mas) {
        toast.error("Bill not found");
        return;
      }

      const billDateYYYY = toYYYYMMDD(data.mas.fld_billdate);

      setFormData((p) => ({
        ...p,
        billGenNo: data.mas.fld_billno || "",
        billDate: billDateYYYY || "",
        billedBy: data.mas.fld_billedby || "",
        remarks: data.mas.fld_remarks || "",
        othersAdditions: data.mas.fld_others ? String(data.mas.fld_others) : "",
      }));

      setDetails(Array.isArray(data.details) ? data.details : []);

      toast.success(`Loaded Bill: ${data.mas.fld_billno}`);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || "Failed to load bill by id");
    } finally {
      setLoadingBill(false);
    }
  };

  // ------------------------------------------------------------------
  // NEW MODE -> generate next bill number
  // ------------------------------------------------------------------
  const loadNextBillNo = async () => {
    if (billid) return;

    try {
      if (!loginuserid) return;

      const res = await axios.get(`${BASE_URL}/api/labourbill/nextBillNo`, {
        params: { loginuserid },
        headers: commonHeaders,
      });

      const data = getObj(res);

      setFormData((p) => ({
        ...p,
        billGenNo: data?.billno || data?.fld_billno || "",
      }));
    } catch (e) {
      console.error(e);
      toast.error("Failed to generate bill number");
    }
  };

  useEffect(() => {
    loadDropdowns();
    loadNextBillNo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadBillById();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billid]);

  // ------------------------------------------------------------------
  // SEARCH labour charges
  // ------------------------------------------------------------------
  const handleSearch = async () => {
    if (!branchid) return toast.error("branchid missing");
    if (!formData.fromDate || !formData.toDate) {
      return toast.error("Please select From Date and To Date");
    }
    if (!formData.projectId || !formData.supplierId) {
      return toast.error("Please select Project and Supplier");
    }

    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/labourbill/labourCharges`, {
        params: {
          branchid,
          fromDate: toDDMMYYYY(formData.fromDate),
          toDate: toDDMMYYYY(formData.toDate),
          supplierid: formData.supplierId,
          projectid: formData.projectId,
        },
        headers: commonHeaders,
      });

      const rows = getRows(res);
      setDetails(rows);
      toast.success(`Loaded ${rows.length} Labour Charges`);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  // ------------------------------------------------------------------
  // SUBMIT: SAVE OR UPDATE
  // ------------------------------------------------------------------
  const handleSubmit = async () => {
    if (!branchid) return toast.error("branchid missing");
    if (!loginuserid) return toast.error("loginuserid missing");

    if (!formData.billDate || !formData.billedBy) {
      return toast.error("Bill Date and Billed By are required");
    }

    if (!details || details.length === 0) {
      return toast.error("No Labour Charges selected/loaded");
    }

    const detailIds = details
      .map((r) => r.fld_LabourChargeId ?? r.fld_LabourChargeID)
      .filter(Boolean);

    const payload = {
      billid: billid || undefined,
      branchid: Number(branchid),
      loginuserid: Number(loginuserid),
      billno: formData.billGenNo,
      billdate: formData.billDate,
      billedby: formData.billedBy,
      remarks: formData.remarks || "",
      othersAdditions: Number(formData.othersAdditions || 0),
      totalAmount: Number(formData.total || 0),
      detailIds,
    };

    try {
      setLoading(true);

      if (billid) {
        const res = await axios.put(`${BASE_URL}/api/labourbill/update`, payload, {
          headers: commonHeaders,
        });
        toast.success(res.data?.message || "Bill updated");
      } else {
        const res = await axios.post(`${BASE_URL}/api/labourbill/save`, payload, {
          headers: commonHeaders,
        });
        toast.success(res.data?.message || "Bill saved");

        const data = getObj(res);
        const newBillId = data?.billid || res.data?.billid;
        if (newBillId) {
          navigate(
            `/PaymentVouchers/LabourChargesBillGenerationEntry?billid=${newBillId}`
          );
        }
      }
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || "Submit failed");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => navigate("/PaymentVouchers/LabourChargesBillListing");

  return (
    <PaymentLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">
              LABOUR CHARGE BILL GENERATION ENTRY {billid ? "(EDIT)" : ""}
            </span>
          }
          setSkin={setSkin}
          currentSkin={skin}
        />
          <button
              onClick={handleBack}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
            isDark
              ? "border-slate-600 text-slate-300 hover:bg-slate-800"
              : "border-slate-300 text-slate-700 hover:bg-slate-100"
          }`}
            >
              <FaArrowLeft />
              Back
            </button>
        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mb-6">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              {billid ? "Update Bill" : "Create Bill"}
            </h1>

            <div className="text-sm opacity-80">
              Branch: <b>{branchid || "-"}</b> | User: <b>{loginuserid || "-"}</b>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                Bill Gen No.
              </label>
              <input
                type="text"
                value={formData.billGenNo}
                className={`w-full px-4 py-2 border rounded-lg ${
                  isDark
                    ? "bg-gray-700 border-gray-600 text-white"
                    : "bg-white border-gray-300 text-gray-900"
                }`}
                readOnly
              />
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                Bill Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.billDate}
                onChange={(e) => handleInputChange("billDate", e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg ${
                  isDark
                    ? "bg-gray-700 border-gray-600 text-white"
                    : "bg-white border-gray-300 text-gray-900"
                }`}
              />
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                Billed By <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.billedBy}
                onChange={(e) => handleInputChange("billedBy", e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg ${
                  isDark
                    ? "bg-gray-700 border-gray-600 text-white"
                    : "bg-white border-gray-300 text-gray-900"
                }`}
                placeholder="Enter billed by"
              />
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                Remarks
              </label>
              <textarea
                rows={3}
                value={formData.remarks}
                onChange={(e) => handleInputChange("remarks", e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg resize-none ${
                  isDark
                    ? "bg-gray-700 border-gray-600 text-white"
                    : "bg-white border-gray-300 text-gray-900"
                }`}
              />
            </div>
          </div>

          {!billid && (
            <div className={`mb-8 p-4 rounded-lg ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <h3 className={`text-lg font-semibold mb-4 ${isDark ? "text-white" : "text-blue-900"}`}>
                Search Labour Charges
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    From Date
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.fromDate}
                    onChange={(e) => handleInputChange("fromDate", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Project
                     <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.projectId}
                    onChange={(e) => handleInputChange("projectId", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {projects.map((p) => (
                      <option key={p.fld_contractid} value={p.fld_contractid}>
                        {p.fld_contractname}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    To Date
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.toDate}
                    onChange={(e) => handleInputChange("toDate", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Supplier
                     <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.supplierId}
                    onChange={(e) => handleInputChange("supplierId", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {suppliers.map((s) => (
                      <option key={s.fld_supplierid} value={s.fld_supplierid}>
                        {s.fld_suppliername}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-center">
                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                    loading
                      ? "bg-gray-400 text-white cursor-not-allowed"
                      : `${buttonClass} ${hoverClass} text-white`
                  }`}
                >
                  <FaSearch />
                  {loading ? "Searching..." : "Search"}
                </button>
              </div>
            </div>
          )}

          <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-4 py-3 text-center font-semibold border-r border-white/20">#</th>
                    <th className="px-4 py-3 text-center font-semibold border-r border-white/20">LC No</th>
                    <th className="px-4 py-3 text-center font-semibold border-r border-white/20">Project</th>
                    <th className="px-4 py-3 text-center font-semibold border-r border-white/20">Supplier</th>
                    <th className="px-4 py-3 text-center font-semibold border-r border-white/20">Entry Date</th>
                    <th className="px-4 py-3 text-center font-semibold">Amount</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loadingBill ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center">
                        Loading Bill...
                      </td>
                    </tr>
                  ) : details.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center">
                        No labour charges loaded.
                      </td>
                    </tr>
                  ) : (
                    details.map((r, idx) => (
                      <tr
                        key={r.fld_LabourChargeId ?? r.fld_LabourChargeID ?? idx}
                        className={`${isDark ? "bg-gray-800" : "bg-white"}`}
                      >
                        <td className="px-4 py-2 text-center">{idx + 1}</td>
                        <td className="px-4 py-2 text-center">
                          {r.fld_LabourChargeNo ?? r.fld_LabourChargeNO}
                        </td>
                        <td className="px-4 py-2 text-center">
                          {r.fld_projectname || ""}
                        </td>
                        <td className="px-4 py-2 text-center">
                          {r.fld_suppliername || ""}
                        </td>
                        <td className="px-4 py-2 text-center">
                          {r.fld_EntryDate || r.fld_entrydate || ""}
                        </td>
                        <td className="px-4 py-2 text-right font-mono">
                          {toFixed2(r.fld_TotalExpenditure ?? r.fld_amount ?? 0)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className={`mt-6 p-4 rounded-lg ${isDark ? "bg-gray-800/50" : "bg-green-50"}`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-2">Others Additions</label>
                <input
                  type="number"
                  value={formData.othersAdditions}
                  onChange={(e) => handleInputChange("othersAdditions", e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Total</label>
                <input
                  type="text"
                  value={formData.total}
                  readOnly
                  className={`w-full px-4 py-2 border rounded-lg font-semibold ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Advance</label>
                <input
                  type="number"
                  value={formData.advance}
                  onChange={(e) => handleInputChange("advance", e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Other Deductions</label>
                <input
                  type="number"
                  value={formData.otherDeductions}
                  onChange={(e) => handleInputChange("otherDeductions", e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Net Amount</label>
                <input
                  type="text"
                  value={formData.netAmount}
                  readOnly
                  className={`w-full px-4 py-2 border rounded-lg font-bold ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-green-400"
                      : "bg-white border-gray-300 text-green-700"
                  }`}
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end items-center gap-6 mt-8">
            <button
              onClick={handleSubmit}
              disabled={loading || loadingBill}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg ${
                loading || loadingBill
                  ? "bg-gray-400 text-white cursor-not-allowed"
                  : `${buttonClass} ${hoverClass} text-white`
              }`}
            >
              {billid ? "Update" : "Submit"}
            </button>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </PaymentLayoutWrapper>
  );
};

export default LabourChargesBillGenerationEntry;